import { defineStore } from 'pinia'
import * as carInfoApi from '../services/api/car-info'

export interface ICarInfoData {
  id: number
  status: string
  type: string
  sale_source: string
  referral_source: string
  note: string
  scheduled_at: string
  car: any
  car_information: any
  customer: any
  assignee: any
  created_at: string
}

export const useCarInfoStore = defineStore('carInfo', {
  state: () => ({
    lastApprovedCarInfo: {
      id: 0,
      assignee_id: 0,
      invoices: [] as any,
      customer: {} as any,
      assignee: {} as any,
      created_at: '',
    },
    carInfo: {
      id: 0,
      status: '',
      type: '',
      sale_source: '',
      referral_source: '',
      note: '',
      scheduled_at: '',
      car: null,
      car_information: {
        brand_id: 0,
        model_id: 0,
        diagnostic: null,
        has_btdb: false,
        has_vcx_due_at: false,
        has_registry_certificate_at: false,
      },
      customer: null,
      assignee: null,
      created_at: '',
    } as ICarInfoData,
    carInfomations: [] as ICarInfoData[],
    carInfomationsByCar: [] as ICarInfoData[],
    count: 0,
    isLoading: false,
    total: 0,
  }),

  actions: {
    setCount(value: number) {
      this.count = value
    },

    setLastApprovedCarInfo(data: any) {
      this.lastApprovedCarInfo = data
    },

    setCarInfomations(data: ICarInfoData[]) {
      this.carInfomations = data
    },

    setCarInfomationsByCar(data: ICarInfoData[]) {
      this.carInfomationsByCar = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    setCarInfo(data: ICarInfoData) {
      this.carInfo = data
    },

    async getCarInfo(carInfo_id: number) {
      try {
        const { data } = await carInfoApi.get(carInfo_id)
        if (data.car) {
          data.has_btdb = !!data.car.btdb
          data.has_vcx_due_at = !!data.car.vcx_due_at
          data.has_registry_certificate_at = !!data.car.registry_certificate_at
        }
        this.setCarInfo(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async updateCarInfo(payload: { carInfo_payload: any; car_info_payload: any }) {
      try {
        const { carInfo_payload, car_info_payload } = payload
        await carInfoApi.update(this.carInfo.id, carInfo_payload)
        await carInfoApi.updateCarInfo(this.carInfo.car_information.id, car_info_payload)
        // Assuming the API returns updated data, you might need to fetch it again or update the state manually
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async reject(payload: any) {
      try {
        const { data } = await carInfoApi.reject(this.carInfo.id, payload)
        this.setCarInfo(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async remake(payload: any) {
      try {
        const { data } = await carInfoApi.remake(this.carInfo.id, payload)
        this.setCarInfo(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async approve(payload: any) {
      try {
        const { data } = await carInfoApi.approve(this.carInfo.id, payload)
        this.setCarInfo(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async reApprove(payload: any) {
      try {
        const { data } = await carInfoApi.reApprove(this.carInfo.id, payload)
        this.setCarInfo(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async createCarInfo() {
      try {
        const { data } = await carInfoApi.createCarInfo({ carInfo_id: this.carInfo.id })
        this.setCarInfo(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    deleteCarInfo(id: number) {
      this.carInfomations = this.carInfomations.filter((carInfo) => carInfo.id !== id)
    },

    addCarInfo(carInfo: ICarInfoData) {
      this.carInfomations.unshift(carInfo)
    },

    async getCount() {
      try {
        const { data } = await carInfoApi.count()
        this.setCount(data)
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async create(payload: any) {
      try {
        const { data } = await carInfoApi.create(payload)
        this.addCarInfo(data)
        return data
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async getLastCarInfo(params: any) {
      try {
        const { data } = await carInfoApi.getLastCarInfo(params)
        this.setLastApprovedCarInfo(data)
        return data
      } catch (e) {
        throw new Error(String(e))
      }
    },

    async listCarInfos(params: any) {
      try {
        this.setLoading(true)
        const result = await carInfoApi.list(params)
        const { data, total } = result as any
        this.setCarInfomations(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listCarInfosByCustomer(params: any) {
      try {
        this.setLoading(true)
        const result = await carInfoApi.listByCustomer(params)
        const { data, total } = result as any
        this.setCarInfomations(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllCarInfosByDirector(params: any) {
      try {
        this.setLoading(true)
        const result = await carInfoApi.listAllByDirector(params)
        const { data, total } = result as any
        this.setCarInfomations(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async destroyCarInfo(id: number) {
      try {
        const { data } = await carInfoApi.destroy(id)
        this.deleteCarInfo(id)
        return data
      } catch (e) {
        throw new Error(String(e))
      }
    },
  },
})
